import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Button, Card, Dialog, EditPane, Field, Input, Segmented, Select, Textarea, toast, useConfirm, type EditPaneTab } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Factory, Plus, Truck } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber, refusalMessage } from "../../api/manufacturing";
import type { OrderDTO } from "../../api/order";
import { WarehouseClient } from "../../api/product";
import {
  SERVICE_INTERNAL_REASONS,
  ServiceMotiveClient,
  ServiceOrderClient,
  ServiceOrderLineClient,
  type ServiceMotiveDTO,
  type ServiceInternalReason,
  type ServiceOrderDTO,
  type ServiceOrderKind,
  type ServiceOrderLineDTO,
} from "../../api/servicedesk";
import { NexpimView } from "../../views";
import { StockMovementsGrid } from "../manufacturing/StockMovementsGrid";
import { internalReasonLabel, ServiceStatusBadge, serviceStatusLabel } from "./badges";
import { NexeraOrderLookup } from "./NexeraOrderLookup";
import { LineDetailsFields, lineFrom, missingForLines, type LineDetails } from "./lineDetails";
import { AvailabilityBadge, ServicePartsPicker, useServiceAvailability, type PickedPart } from "./ServicePartsPicker";
import { canMakeAtPlant, MakeAtPlantDialog, MakingChip } from "./MakeAtPlantDialog";
import { ServiceProductionTab } from "./ServiceProductionTab";
import { ShipDialog } from "./ShipDialog";

/**
 * One service order: the parts it sends, who they go to, and what has left.
 *
 * <p>What can be done follows the status, as the server enforces it: an open
 * order is edited and shipped — shipping everything closes it — or closed
 * short, or cancelled if nothing has left; a closed one can be reopened for
 * the customer who calls back. Lines are frozen once the order is closed.</p>
 */
export function ServiceOrderDetailPage({ orderId }: { orderId: number }) {
  // Keyed on the order, so dialogs and unsaved edits never carry over to another order on the same route.
  return <ServiceOrderDetail key={orderId} orderId={orderId} />;
}

function ServiceOrderDetail({ orderId }: { orderId: number }) {
  const { t } = useTranslation();
  const { http, hasView, canWrite } = useAuth();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const allowed = hasView(NexpimView.SERVICE_DESK);
  const orders = useMemo(() => new ServiceOrderClient(http), [http]);
  const lines = useMemo(() => new ServiceOrderLineClient(http), [http]);
  const [shipping, setShipping] = useState(false);
  // The Customer tab's unsaved edits live here: switching tabs unmounts it, and adding a
  // line refetches the order, which must not wipe what was typed.
  const [customerEdits, setCustomerEdits] = useState<Partial<ServiceOrderDTO>>({});

  const order = useQuery({
    queryKey: ["servicedesk", "service-order", orderId],
    queryFn: () => orders.findById(orderId),
    enabled: allowed,
  });
  const orderLines = useQuery({
    queryKey: ["servicedesk", "service-order-line", orderId],
    queryFn: async () =>
      (await lines.find({ page: 0, size: 500, filters: { service_order_id: [String(orderId)] }, sort: ["position,asc"] })).content,
    enabled: allowed,
  });
  const motives = useQuery({
    queryKey: ["servicedesk", "service-motive", "all"],
    queryFn: () => new ServiceMotiveClient(http).findAll(),
    staleTime: 10 * 60_000,
    enabled: allowed,
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["servicedesk"] });
    await queryClient.invalidateQueries({ queryKey: ["manufacturing"] });
  };

  const act = useMutation({
    mutationFn: (action: "close" | "cancel" | "reopen") => orders[action](orderId),
    onSuccess: async (updated) => {
      await refresh();
      toast.success(t("sd.now", "Service order {{n}} is now {{status}}", { n: updated.number, status: serviceStatusLabel(t, updated.status).toLowerCase() }));
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const remove = useMutation({
    mutationFn: () => orders.remove(orderId),
    onSuccess: async () => {
      await refresh();
      toast.success(t("notification.deleted", "Deleted"));
      void navigate({ to: "/servicedesk/orders" });
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const parent = { to: "/servicedesk/orders", label: t("navigation.item.service-orders", "Service orders") };
  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.service-orders", "Service orders")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">{t("error.access-denied.detail", "Your account does not have permission to view this page.")}</p>
        </div>
      </>
    );
  }
  const data = order.data;
  if (!data) {
    return (
      <>
        <PageHeader parent={parent} title={t("navigation.item.service-orders", "Service orders")} />
        <div className="p-4 text-[13px] text-ink-muted lg:p-6">
          {order.isLoading ? t("common.loading", "Loading…") : t("sd.not-found", "That service order could not be loaded.")}
        </div>
      </>
    );
  }

  const rows = orderLines.data ?? [];
  const open = data.status === "OPEN";
  const shippedAny = rows.some((line) => Number(line.quantity_shipped ?? 0) > 0);
  // One action at a time: a second click while the first is on its way would act on a stale status.
  const busy = act.isPending || remove.isPending;
  const ask = async (action: "close" | "cancel", title: string, message: string) => {
    if (await confirm({ title, message, confirmLabel: title, variant: action === "cancel" ? "danger" : "primary" })) {
      act.mutate(action);
    }
  };

  const tabs: EditPaneTab[] = [
    {
      id: "lines",
      flush: true,
      label: t("sd.tab.lines", "Parts"),
      badge: data.open_line_count ?? 0,
      content: (
        <LinesTab
          order={data}
          rows={rows}
          loading={orderLines.isLoading}
          motives={motives.data ?? []}
          editable={canWrite && open}
          onChanged={refresh}
        />
      ),
    },
    {
      id: "production",
      flush: true,
      label: t("sd.tab.production", "Production"),
      badge: new Set(rows.map((line) => line.making_lot_id).filter((id) => id != null)).size || undefined,
      content: <ServiceProductionTab orderId={orderId} />,
    },
    {
      id: "customer",
      label: t("sd.tab.customer", "Customer"),
      content: <CustomerTab order={data} edits={customerEdits} onEdit={setCustomerEdits} editable={canWrite && open} onSaved={refresh} />,
    },
    {
      id: "shipments",
      flush: true,
      label: t("sd.tab.shipments", "Shipments"),
      content: <StockMovementsGrid serviceOrderId={orderId} />,
    },
  ];

  return (
    <>
      <PageHeader
        parent={parent}
        title={t("sd.title", "Service order {{n}}", { n: data.number })}
        subtitle={[
          data.kind === "INTERNAL"
            ? `${t("sd.kind.internal", "Internal")}${data.internal_reason ? ` · ${internalReasonLabel(t, data.internal_reason)}` : ""}`
            : (data.client_name ?? t("sd.no-client", "No client")),
          data.kind === "INTERNAL" ? data.requested_by : data.ship_name,
          data.ship_city,
        ]
          .filter(Boolean)
          .join(" · ")}
        badges={
          <>
            <ServiceStatusBadge status={data.status} />
            {data.client_order_reference && (
              <Badge variant="neutral" size="sm">
                {data.client_order_reference}
              </Badge>
            )}
            {data.nexera_order_number != null && (
              <Badge variant="neutral" size="sm">
                Nexera #{data.nexera_order_number}
              </Badge>
            )}
            {data.sigm_order_id != null && (
              <Badge variant="neutral" size="sm">
                SIGM{data.sigm_agent ? ` · ${data.sigm_agent}` : ""}
              </Badge>
            )}
          </>
        }
        actions={
          canWrite ? (
            <>
              {open && !shippedAny && (
                <Button
                  variant="ghost"
                  disabled={busy}
                  loading={remove.isPending}
                  onClick={async () => {
                    if (
                      await confirm({
                        title: t("sd.delete.title", "Delete this service order?"),
                        message: t("sd.delete.message", "Nothing has shipped on it, so it can go entirely."),
                        variant: "danger",
                        confirmLabel: t("po.delete", "Delete"),
                      })
                    ) {
                      remove.mutate();
                    }
                  }}
                >
                  {t("po.delete", "Delete")}
                </Button>
              )}
              {open && !shippedAny && (
                <Button variant="ghost" disabled={busy} onClick={() => void ask("cancel", t("sd.cancel", "Cancel order"), t("sd.cancel.message", "Nothing has shipped. It stays on file as cancelled."))}>
                  {t("sd.cancel", "Cancel order")}
                </Button>
              )}
              {open && (
                <Button variant="ghost" disabled={busy} onClick={() => void ask("close", t("sd.close", "Close"), t("sd.close.message", "Nothing more will be sent on this order, whatever is still to ship."))}>
                  {t("sd.close", "Close")}
                </Button>
              )}
              {open && (
                <Button variant="primary" disabled={busy || rows.length === 0} onClick={() => setShipping(true)}>
                  <Truck className="size-4" />
                  {t("sd.ship", "Ship")}
                </Button>
              )}
              {data.status === "CLOSED" && (
                <Button variant="ghost" disabled={busy} loading={act.isPending} onClick={() => act.mutate("reopen")}>
                  {t("po.reopen", "Reopen")}
                </Button>
              )}
            </>
          ) : null
        }
      />
      <EditPane tabs={tabs} urlParam="tab" />
      {shipping && <ShipDialog order={data} lines={rows} onClose={() => setShipping(false)} onPosted={refresh} />}
    </>
  );
}

function LinesTab({
  order,
  rows,
  loading,
  motives,
  editable,
  onChanged,
}: {
  order: ServiceOrderDTO;
  rows: ServiceOrderLineDTO[];
  loading: boolean;
  motives: ServiceMotiveDTO[];
  editable: boolean;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const lines = useMemo(() => new ServiceOrderLineClient(http), [http]);
  const nextPosition = Math.max(0, ...rows.map((line) => line.position)) + 1;
  const activeMotives = motives.filter((motive) => motive.is_active);

  const [adding, setAdding] = useState(false);
  // Lines ticked to send to the plant, and the dialog that sends them.
  const [selected, setSelected] = useState<ServiceOrderLineDTO[]>([]);
  const [making, setMaking] = useState(false);
  const makeable = selected.filter(canMakeAtPlant);
  // Whether what is still to send can go from the service shelf: only asked for
  // lines with something outstanding, on an order that can still ship.
  const waiting = order.status === "OPEN" ? rows.filter((line) => Number(line.quantity_outstanding ?? 0) > 0) : [];
  const availability = useServiceAvailability(
    waiting.map((line) => line.part_id),
    order.id,
  );

  const cols = useMemo(() => {
    const c = defineColumns<ServiceOrderLineDTO>();
    return [
      c.custom(
        "part",
        (row) => row.part_number ?? "",
        (row) => (
          <span>
            <span className="font-medium">{row.part_number}</span>
            <span className="ml-2 text-[11px] text-ink-faint">{row.part_name}</span>
            {row.is_made_to_order && (
              <Badge variant="info" size="sm" className="ml-2">
                {t("sd.line.made", "made at the plant")}
              </Badge>
            )}
          </span>
        ),
        { headerKey: "column.bom.component", priority: 1 },
      ),
      c.text("quantity", (row) => formatNumber(row.quantity), { headerKey: "column.bom.quantity", priority: 1 }),
      c.custom(
        "shipped",
        (row) => row.quantity_shipped ?? 0,
        (row) => (
          <span className="tabular-nums">
            {formatNumber(row.quantity_shipped)}
            {Number(row.quantity_outstanding ?? 0) > 0 && (
              <span className="ml-1.5 text-[11px] text-warn">{t("sd.line.to-ship", "{{n}} to ship", { n: formatNumber(row.quantity_outstanding) })}</span>
            )}
          </span>
        ),
        { headerKey: "column.sd.shipped", priority: 2 },
      ),
      c.custom(
        "shelf",
        (row) => availability.get(row.part_id)?.available ?? 0,
        (row) =>
          order.status === "OPEN" && Number(row.quantity_outstanding ?? 0) > 0 ? (
            <AvailabilityBadge availability={availability.get(row.part_id)} needed={Number(row.quantity_outstanding)} />
          ) : null,
        { headerKey: "column.sd.shelf", priority: 2 },
      ),
      c.custom("making", (row) => row.making_component_code ?? "", (row) => <MakingChip line={row} />, {
        headerKey: "column.sd.making",
        priority: 2,
      }),
      c.text("motive", (row) => (row.service_motive_code ? `${row.service_motive_code} · ${row.service_motive_name ?? ""}` : ""), {
        headerKey: "column.sd.motive",
        priority: 2,
      }),
      c.custom(
        "product_sku",
        (row) => row.product_sku ?? "",
        (row) =>
          row.product_id != null ? (
            <Link to="/products/$productId" params={{ productId: String(row.product_id) }} className="text-accent hover:underline">
              {row.product_sku}
            </Link>
          ) : (
            <span>{row.product_sku}</span>
          ),
        { headerKey: "column.sd.product", priority: 2 },
      ),
      c.custom(
        "production_reference",
        (row) => row.production_reference ?? "",
        (row) =>
          row.production_order_id != null ? (
            <Link to="/production/orders/$orderId" params={{ orderId: String(row.production_order_id) }} className="text-accent hover:underline">
              {row.production_reference}
            </Link>
          ) : (
            <span>{row.production_reference}</span>
          ),
        { headerKey: "column.sd.run", priority: 3 },
      ),
      c.text("quantity_cancelled", (row) => (Number(row.quantity_cancelled) > 0 ? formatNumber(row.quantity_cancelled) : ""), {
        headerKey: "column.po.cancelled",
        defaultVisible: false,
      }),
      c.text("requested_from_plant_date", (row) => row.requested_from_plant_date ?? "", { headerKey: "column.sd.requested", defaultVisible: false }),
      c.text("made_date", (row) => row.made_date ?? "", { headerKey: "column.sd.made", defaultVisible: false }),
      c.text("ready_date", (row) => row.ready_date ?? "", { headerKey: "column.sd.ready", defaultVisible: false }),
      c.text("note", (row) => row.note ?? "", { headerKey: "column.note", priority: 4 }),
    ];
  }, [t, availability, order.status]);

  const date = (key: "requested_from_plant_date" | "made_date" | "ready_date") => ({
    kind: "text" as const,
    get: (row: ServiceOrderLineDTO) => row[key],
    set: (row: ServiceOrderLineDTO, value: string) => ({ ...row, [key]: value.trim() === "" ? null : value.trim() }),
    placeholder: "YYYY-MM-DD",
  });
  // An empty number cell stays NaN, so validate refuses it instead of saving a zero nobody typed.
  const number = (value: string) => (value.trim() === "" ? Number.NaN : Number(value));
  const shown = (value: number) => (Number.isNaN(value) ? "" : value);
  const plantDates = (line: ServiceOrderLineDTO) =>
    line.requested_from_plant_date != null || line.made_date != null || line.ready_date != null;
  // A line's motive may since have been retired: it stays a choice on that line, so the cell does not show another one.
  const motiveOptions = (row: ServiceOrderLineDTO) =>
    motives
      .filter((motive) => motive.is_active || motive.id === row.service_motive_id)
      .map((motive) => ({ value: String(motive.id), label: `${motive.code} · ${motive.name}` }));
  const failed = (failure: unknown) => new Error(refusalMessage(failure, t("error.generic", "Something went wrong.")));

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {editable && (
        <div className="flex flex-wrap items-center gap-2 border-b border-line bg-surface px-4 py-2 lg:px-6">
          <Button variant="primary" size="sm" onClick={() => setAdding(true)}>
            <Plus className="size-4" />
            {t("sd.line.add-button", "Add part")}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={makeable.length === 0}
            title={makeable.length === 0 ? t("sd.plant.select-hint", "Tick lines with something left to send that no lot is making yet.") : undefined}
            onClick={() => setMaking(true)}
          >
            <Factory className="size-4" />
            {makeable.length > 0
              ? t("sd.plant.action-n", "Make {{n}} at the plant", { n: makeable.length })
              : t("sd.plant.action", "Make at the plant")}
          </Button>
          {rows.length > 0 && (
            <span className="text-[12px] text-ink-muted">{t("sd.line.edit-hint", "The pencil at the end of a line changes its quantity, motive, product or run.")}</span>
          )}
        </div>
      )}
      {making && <MakeAtPlantDialog lines={selected} excludeOrderId={order.id} onClose={() => setMaking(false)} />}
      {adding && (
        <AddLineDialog
          order={order}
          position={nextPosition}
          motives={activeMotives}
          onClose={() => setAdding(false)}
          onAdded={async () => {
            setAdding(false);
            await onChanged();
          }}
        />
      )}
      <DataTable<ServiceOrderLineDTO>
        data={rows}
        columns={cols}
        getRowId={(row) => String(row.id)}
        loading={loading}
        selectable={editable}
        onSelectionChange={setSelected}
        editor={
          editable
            ? {
                fields: {
                  quantity: { kind: "number", get: (row) => shown(row.quantity), set: (row, value) => ({ ...row, quantity: number(value) }), required: true },
                  motive: {
                    kind: "select",
                    get: (row) => (row.service_motive_id == null ? "" : String(row.service_motive_id)),
                    set: (row, value) => ({ ...row, service_motive_id: value === "" ? null : Number(value) }),
                    options: motiveOptions,
                    required: true,
                  },
                  product_sku: {
                    kind: "text",
                    get: (row) => row.product_sku,
                    set: (row, value) => ({ ...row, product_sku: value === "" ? null : value }),
                    placeholder: t("sd.line.sku", "SKU"),
                  },
                  production_reference: {
                    kind: "text",
                    get: (row) => row.production_reference,
                    set: (row, value) => ({ ...row, production_reference: value === "" ? null : value }),
                    placeholder: t("sd.line.run", "Run on the box label"),
                  },
                  quantity_cancelled: {
                    kind: "number",
                    get: (row) => shown(row.quantity_cancelled),
                    // Empty is none cancelled.
                    set: (row, value) => ({ ...row, quantity_cancelled: value.trim() === "" ? 0 : Number(value) }),
                  },
                  requested_from_plant_date: date("requested_from_plant_date"),
                  made_date: date("made_date"),
                  ready_date: date("ready_date"),
                  note: { kind: "text", get: (row) => row.note, set: (row, value) => ({ ...row, note: value === "" ? null : value }) },
                },
                validate: (row) => {
                  const errors: Record<string, string> = {};
                  if (!(row.quantity > 0)) errors.quantity = t("sd.line.quantity-invalid", "Enter a quantity above zero.");
                  if (!(row.quantity_cancelled >= 0) || row.quantity_cancelled > row.quantity) {
                    errors.quantity_cancelled = t("sd.line.cancelled-invalid", "Between zero and the quantity.");
                  }
                  if (row.service_motive_id == null) errors.motive = t("sd.line.motive-required", "Choose a motive.");
                  for (const key of ["requested_from_plant_date", "made_date", "ready_date"] as const) {
                    const value = row[key];
                    if (value != null && !(/^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(value)))) {
                      errors[key] = t("sd.line.date-invalid", "A date as YYYY-MM-DD.");
                    }
                  }
                  return errors;
                },
                onSave: async (row) => {
                  // A plant date entered means the part is made to order; clearing the
                  // dates that made it so takes it back. A line flagged without dates keeps its flag.
                  const before = rows.find((line) => line.id === row.id);
                  const made = plantDates(row) || (row.is_made_to_order && !(before != null && plantDates(before)));
                  try {
                    await lines.update({ ...row, is_made_to_order: made });
                  } catch (failure) {
                    throw failed(failure);
                  }
                  await onChanged();
                  toast.success(t("notification.saved", "Saved"));
                },
                onDelete: async (row) => {
                  try {
                    await lines.remove(row.id);
                  } catch (failure) {
                    throw failed(failure);
                  }
                  await onChanged();
                  toast.success(t("notification.deleted", "Deleted"));
                },
                deleteConfirm: () => ({ title: t("bom.line.delete", "Remove this line?") }),
              }
            : undefined
        }
        emptyMessage={t("sd.lines.empty", "No parts on this order yet.")}
      />
    </div>
  );
}

/**
 * Parts to add to an open order: picked from the linked sale or found by
 * search (see ServicePartsPicker), with one motive, run and note for all.
 */
function AddLineDialog({
  order,
  position,
  motives,
  onClose,
  onAdded,
}: {
  order: ServiceOrderDTO;
  position: number;
  motives: ServiceMotiveDTO[];
  onClose: () => void;
  onAdded: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const lines = useMemo(() => new ServiceOrderLineClient(http), [http]);
  const [picked, setPicked] = useState<Record<string, PickedPart>>({});
  const [details, setDetails] = useState<LineDetails>({ motiveId: "", run: "", note: "" });
  const chosen = Object.values(picked);

  const add = useMutation({
    mutationFn: async () => {
      let next = position;
      for (const part of chosen) {
        await lines.create(lineFrom(part, order.id, next++, details));
      }
    },
    onSuccess: async () => {
      await onAdded();
      toast.success(chosen.length === 1 ? t("sd.line.added-one", "1 part added") : t("sd.line.added-n", "{{n}} parts added", { n: chosen.length }));
    },
    onError: async (failure) => {
      // Lines created before the failure stay; the order shows them.
      await onAdded();
      toast.error(refusalMessage(failure, t("error.generic", "Something went wrong.")));
    },
  });

  const missing = missingForLines(t, chosen, details);

  return (
    <Dialog
      open
      size="xl"
      onClose={onClose}
      title={t("sd.line.add-title", "Add parts to send")}
      actions={
        <>
          {missing && <span className="mr-auto text-[12px] text-ink-muted">{missing}</span>}
          <Button variant="ghost" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button variant="primary" disabled={chosen.length === 0 || missing != null} loading={add.isPending} onClick={() => add.mutate()}>
            {chosen.length > 1 ? t("sd.line.add-n", "Add {{n}} parts", { n: chosen.length }) : t("sd.line.add-button", "Add part")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {order.nexera_order_number == null && (
          <p className="text-[13px] text-ink-muted">
            {order.kind === "INTERNAL"
              ? t("sd.line.internal", "An internal order has no sale to pick from: search the parts below.")
              : t("sd.line.no-sale", "This order is not linked to a sale, so there is nothing sold to pick from. Link the sale on the Customer tab, or search a part below.")}
          </p>
        )}
        <ServicePartsPicker saleNumber={order.nexera_order_number} excludeOrderId={order.id} picked={picked} onChange={setPicked} />
        <LineDetailsFields motives={motives} value={details} onChange={setDetails} />
      </div>
    </Dialog>
  );
}

function CustomerTab({
  order,
  edits,
  onEdit,
  editable,
  onSaved,
}: {
  order: ServiceOrderDTO;
  edits: Partial<ServiceOrderDTO>;
  onEdit: (edits: Partial<ServiceOrderDTO>) => void;
  editable: boolean;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const orders = useMemo(() => new ServiceOrderClient(http), [http]);
  const [relinking, setRelinking] = useState(false);
  const draft: ServiceOrderDTO = { ...order, ...edits };
  const dirty = Object.entries(edits).some(([key, value]) => value !== order[key as keyof ServiceOrderDTO]);
  const setDraft = (next: ServiceOrderDTO) => {
    const changed: Partial<ServiceOrderDTO> = {};
    for (const key of Object.keys(next) as Array<keyof ServiceOrderDTO>) {
      if (next[key] !== order[key]) (changed as Record<string, unknown>)[key] = next[key];
    }
    onEdit(changed);
  };
  // The client follows the sale and the server sets it on save. A newly linked sale's client
  // is asked of the server, which applies the same rule (a replacement billed to the plant is
  // traced back to the sale), so the field shows what saving will give.
  const relinked = draft.nexera_order_number !== order.nexera_order_number;
  const preview = useQuery({
    queryKey: ["servicedesk", "sale-client", draft.nexera_order_number],
    queryFn: () => orders.saleClient(draft.nexera_order_number!),
    enabled: relinked && draft.nexera_order_number != null,
    staleTime: 5 * 60_000,
  });

  const warehouses = useQuery({
    queryKey: ["warehouse", "all"],
    queryFn: () => new WarehouseClient(http).findAll(),
    staleTime: 30 * 60_000,
  });

  const save = useMutation({
    mutationFn: () => orders.update(draft),
    onSuccess: async () => {
      onEdit({});
      await onSaved();
      toast.success(t("notification.saved", "Saved"));
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const text = (value: string) => (value.trim() === "" ? null : value);
  const input = (key: "ship_name" | "ship_name_2" | "ship_address" | "ship_city" | "ship_postal_code" | "ship_phone" | "ship_email" | "client_order_reference", label: string) => (
    <Field label={label} htmlFor={`sd-${key}`}>
      <Input id={`sd-${key}`} value={draft[key] ?? ""} disabled={!editable} onChange={(event) => setDraft({ ...draft, [key]: text(event.target.value) })} />
    </Field>
  );
  // The client shown follows the sale; the server sets the real one from it on save.
  const link = (sale: OrderDTO) => {
    const number = sale.no == null ? null : Number(sale.no);
    setDraft({
      ...draft,
      nexera_order_number: number,
      client_order_reference: draft.client_order_reference ?? sale.client_order_no?.trim() ?? null,
      // Picking the sale already linked gives back its client as saved.
      ...(number === order.nexera_order_number
        ? { client_id: order.client_id, client_number: order.client_number ?? null, client_name: order.client_name ?? null }
        : {}),
    });
    setRelinking(false);
  };
  const unlink = () => {
    setDraft({ ...draft, nexera_order_number: null });
    setRelinking(false);
  };
  const clientLabel = relinked
    ? draft.nexera_order_number == null
      ? t("sd.no-client", "No client")
      : preview.data
        ? `${preview.data.client_name}${preview.data.client_number ? ` (${preview.data.client_number})` : ""}`
        : preview.data === null
          ? t("sd.no-client", "No client")
          : t("sd.client.on-save", "From Nexera order #{{n}}, once saved", { n: draft.nexera_order_number })
    : order.client_name
      ? `${order.client_name}${order.client_number ? ` (${order.client_number})` : ""}`
      : t("sd.no-client", "No client");

  const internal = draft.kind === "INTERNAL";
  // Switching to Internal drops the sale (the server would anyway); switching back asks for one again.
  const setKind = (kind: ServiceOrderKind) =>
    setDraft(
      kind === "INTERNAL"
        ? { ...draft, kind, nexera_order_number: null, internal_reason: draft.internal_reason ?? "OTHER" }
        : { ...draft, kind, internal_reason: null, requested_by: null },
    );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <Segmented
          value={draft.kind}
          onChange={(value) => editable && setKind(value as ServiceOrderKind)}
          options={[
            { value: "ORDER", label: t("sd.kind.order", "Customer order") },
            { value: "INTERNAL", label: t("sd.kind.internal", "Internal") },
          ]}
        />
      </div>
      {internal ? (
        <Card className="p-4 sm:p-5">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Field label={t("sd.reason", "Why")} htmlFor="sd-reason" required>
              <Select
                id="sd-reason"
                value={draft.internal_reason ?? ""}
                disabled={!editable}
                options={SERVICE_INTERNAL_REASONS.map((value) => ({ value, label: internalReasonLabel(t, value) }))}
                onChange={(event) => setDraft({ ...draft, internal_reason: event.target.value as ServiceInternalReason })}
              />
            </Field>
            <Field label={t("sd.requested-by", "Requested by")} htmlFor="sd-requested">
              <Input
                id="sd-requested"
                value={draft.requested_by ?? ""}
                disabled={!editable}
                onChange={(event) => setDraft({ ...draft, requested_by: text(event.target.value) })}
              />
            </Field>
            <Field label={t("column.sd.date", "Date")} htmlFor="sd-date">
              <Input id="sd-date" type="date" value={draft.order_date ?? ""} disabled={!editable} onChange={(event) => setDraft({ ...draft, order_date: event.target.value || null })} />
            </Field>
            <Field label={t("sd.ship-from", "Ship from")} htmlFor="sd-warehouse">
              <Select
                id="sd-warehouse"
                value={String(draft.warehouse_id)}
                disabled={!editable}
                options={(warehouses.data ?? [])
                  .filter((w) => (w.is_active && (w.kind === "SERVICE" || w.kind === "MANUFACTURING")) || w.id === draft.warehouse_id)
                  .map((w) => ({ value: String(w.id), label: `${w.code} · ${w.name ?? ""}` }))}
                onChange={(event) => setDraft({ ...draft, warehouse_id: Number(event.target.value) })}
              />
            </Field>
          </div>
        </Card>
      ) : (
      <Card className="p-4 sm:p-5">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label={t("column.sd.client", "Client")} htmlFor="sd-client">
            <Input
              id="sd-client"
              value={clientLabel}
              disabled
            />
          </Field>
          {input("client_order_reference", t("column.sd.reference", "Retailer order"))}
          <Field label={t("column.sd.nexera-order", "Nexera order")} htmlFor="sd-nexera">
            <div className="flex items-center gap-2">
              <Input id="sd-nexera" value={draft.nexera_order_number ?? ""} disabled />
              {editable && (
                <Button variant="ghost" size="sm" onClick={() => setRelinking(!relinking)}>
                  {relinking ? t("dialog.cancel", "Cancel") : t("sd.relink", "Find")}
                </Button>
              )}
              {editable && draft.nexera_order_number != null && (
                <Button variant="ghost" size="sm" onClick={unlink}>
                  {t("sd.unlink", "Unlink")}
                </Button>
              )}
            </div>
          </Field>
          {relinking && (
            <div className="sm:col-span-2 lg:col-span-3">
              <NexeraOrderLookup initial={draft.client_order_reference ?? ""} onPick={link} />
            </div>
          )}
          <Field label={t("column.sd.date", "Date")} htmlFor="sd-date">
            <Input id="sd-date" type="date" value={draft.order_date ?? ""} disabled={!editable} onChange={(event) => setDraft({ ...draft, order_date: event.target.value || null })} />
          </Field>
          <Field label={t("sd.ship-from", "Ship from")} htmlFor="sd-warehouse">
            <Select
              id="sd-warehouse"
              value={String(draft.warehouse_id)}
              disabled={!editable}
              options={(warehouses.data ?? [])
                .filter((w) => (w.is_active && (w.kind === "SERVICE" || w.kind === "MANUFACTURING")) || w.id === draft.warehouse_id)
                .map((w) => ({ value: String(w.id), label: `${w.code} · ${w.name ?? ""}` }))}
              onChange={(event) => setDraft({ ...draft, warehouse_id: Number(event.target.value) })}
            />
          </Field>
        </div>
      </Card>
      )}
      <Card className="p-4 sm:p-5">
        <h3 className="mb-3 text-[13px] font-semibold">{t("sd.ship-to", "Ship to")}</h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {input("ship_name", t("column.sd.customer", "Customer"))}
          {input("ship_name_2", t("sd.name-2", "Apartment, suite"))}
          {input("ship_address", t("sd.address", "Address"))}
          {input("ship_city", t("column.sd.city", "City"))}
          {input("ship_postal_code", t("sd.postal-code", "Postal code"))}
          {input("ship_phone", t("sd.phone", "Phone"))}
          {input("ship_email", t("column.sd.email", "Email"))}
          <Field label={t("form.field.note", "Note")} htmlFor="sd-note" className="sm:col-span-2 lg:col-span-3">
            <Textarea id="sd-note" rows={3} value={draft.note ?? ""} disabled={!editable} onChange={(event) => setDraft({ ...draft, note: text(event.target.value) })} />
          </Field>
        </div>
      </Card>
      {editable && (
        <div>
          <Button variant="primary" disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>
            {t("button.save", "Save")}
          </Button>
        </div>
      )}
    </div>
  );
}
